import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { Plus, TrendingUp, Search, Calendar, IndianRupee, CheckCircle, Clock, Eye, Filter, ArrowUpDown, AlertCircle, CreditCard, Users, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor } from '@/utils/helpers';
import { formatFullName } from '@/utils/nameFormatter';

function LoanManagement() {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [approvingLoan, setApprovingLoan] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedLoanSchedule, setSelectedLoanSchedule] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Collapsible state for guarantor sections
  const [guarantor1Open, setGuarantor1Open] = useState(false);
  const [guarantor2Open, setGuarantor2Open] = useState(false);

  // Filter and Sort States
  const [filters, setFilters] = useState({
    status: 'all',
    showOverdue: false,
  });
  const [sortBy, setSortBy] = useState('date_desc'); // date_desc, date_asc, amount_desc, amount_asc, overdue_desc

  const [formData, setFormData] = useState({
    account_holder_id: '',
    principal_amount: '',
    monthly_interest_rate: '0.5',
    tenure_months: '10',
    borrower_aadhar_card: '',
    borrower_pan_card: '',
    guarantor1_name: '',
    guarantor1_phone: '',
    guarantor1_relationship: '',
    guarantor2_name: '',
    guarantor2_phone: '',
    guarantor2_relationship: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch members who opted in for loans
      const membersData = await api.accountHolders.getAll();
      setMembers(membersData.filter(m => m.opts_in_loan));

      // Fetch loans
      const loansData = await api.loans.getAll();

      // Enrich loans with member names
      const enrichedLoans = loansData.map(loan => {
        if (loan.account_holder_name) return loan;

        const member = membersData.find(m => m.id === loan.account_holder_id);
        return {
          ...loan,
          account_holder_name: member ? formatFullName(member.first_name, member.middle_name, member.last_name) : 'Unknown'
        };
      });

      setLoans(enrichedLoans);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const calculateLoanDetails = () => {
    const P = parseFloat(formData.principal_amount) || 0;
    const R_m = parseFloat(formData.monthly_interest_rate) || 0;
    const N = parseInt(formData.tenure_months) || 1;

    // Mandali Logic: Interest is DEDUCTED UPFRONT
    const totalInterest = P * (R_m / 100) * N;
    const disbursedAmount = P - totalInterest; // Amount given to borrower (on hand)
    const monthlyEMI = P / N; // EMI is principal only (interest already deducted)
    const totalPayable = P; // Borrower pays back only principal

    return {
      principal: P,
      interest: totalInterest,
      disbursedAmount: disbursedAmount,
      monthlyInstallment: monthlyEMI,
      totalRepayment: totalPayable
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const details = calculateLoanDetails();

    // Confirmation Dialog - Updated for Mandali logic
    if (!window.confirm(`Please confirm loan details:\n\nPrincipal Amount: ₹${details.principal.toLocaleString()}\nMonthly Rate: ${formData.monthly_interest_rate}%\nTenure: ${formData.tenure_months} months\n\nTotal Interest (Deducted Upfront): - ₹${details.interest.toFixed(0)}\nDisbursed Amount (On Hand): ₹${details.disbursedAmount.toFixed(0)}\n\nMonthly EMI: ₹${details.monthlyInstallment.toFixed(0)}\nTotal Payable: ₹${details.totalRepayment.toFixed(0)}\n\nProceed?`)) {
      return;
    }

    setSubmitting(true);

    try {
      const loanData = {
        ...formData,
        principal_amount: parseFloat(formData.principal_amount),
        monthly_interest_rate: parseFloat(formData.monthly_interest_rate),
        tenure_months: parseInt(formData.tenure_months),
        // Backend expects these but we don't use them for calculation anymore
        interest_rate_annual: 0,
      };

      await api.loans.apply(loanData);
      toast.success('Loan application submitted successfully!');
      setDialogOpen(false);
      fetchData();

      // Reset form
      setFormData({
        account_holder_id: '',
        principal_amount: '',
        interest_rate_annual: '0',
        tenure_months: '10',
        borrower_aadhar_card: '',
        borrower_pan_card: '',
        guarantor1_name: '',
        guarantor1_phone: '',
        guarantor1_relationship: '',
        guarantor2_name: '',
        guarantor2_phone: '',
        guarantor2_relationship: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply for loan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveLoan = async (loanId) => {
    setApprovingLoan(loanId);
    try {
      await api.loans.approve(loanId);
      toast.success('Loan approved and disbursed successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve loan');
    } finally {
      setApprovingLoan(null);
    }
  };

  const viewSchedule = async (loanId) => {
    try {
      const schedule = await api.loans.getSchedule(loanId);
      setSelectedLoanSchedule(schedule);
      setScheduleDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load loan schedule');
    }
  };

  // Filter and Sort Logic
  const filteredAndSortedLoans = useMemo(() => {
    let filtered = [...loans];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(loan =>
        loan.loan_account_number?.toLowerCase().includes(query) ||
        loan.account_holder_name?.toLowerCase().includes(query) ||
        loan.account_holder_id?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(loan => loan.status === filters.status);
    }

    // Apply overdue filter
    if (filters.showOverdue) {
      filtered = filtered.filter(loan => {
        // Calculate if loan has overdue EMIs
        const today = new Date();
        return loan.status === 'active' && loan.outstanding_installments > 0;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'amount_desc':
          return b.principal_amount - a.principal_amount;
        case 'amount_asc':
          return a.principal_amount - b.principal_amount;
        case 'overdue_desc':
          return (b.outstanding_principal || 0) - (a.outstanding_principal || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [loans, searchQuery, filters, sortBy]);

  // Calculate quick stats
  const quickStats = useMemo(() => {
    const activeLoans = loans.filter(l => l.status === 'active').length;
    const totalOutstanding = loans
      .filter(l => l.status === 'active')
      .reduce((sum, l) => sum + (l.outstanding_principal || 0), 0);

    // Calculate ACTUAL overdue loans: loan_end_date < today AND outstanding > 0
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const overdueCount = loans.filter(l => {
      if (l.status !== 'active' || !l.loan_end_date) return false;
      const endDate = new Date(l.loan_end_date);
      endDate.setHours(0, 0, 0, 0);
      return endDate < today && (l.outstanding_principal || 0) > 0;
    }).length;

    // Due This Month (20th): All active loans with outstanding installments and next due = 20th of current month
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const dueThisMonth = loans.filter(l => {
      if (l.status !== 'active' || (l.outstanding_installments || 0) === 0) return false;
      // Next due date is 20th of current month (if today < 20) or next month (if today >= 20)
      const currentDay = today.getDate();
      if (currentDay < 20) {
        // Due this month on 20th
        return true;
      }
      return false; // Already passed this month's due date
    }).length;

    return {
      activeLoans,
      totalOutstanding,
      overdueCount,
      dueThisMonth
    };
  }, [loans]);

  const resetFilters = () => {
    setFilters({
      status: 'all',
      showOverdue: false,
    });
    setSortBy('date_desc');
    setSearchQuery('');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in pt-2">
        {/* Header - Consistent spacing */}
        <div className="flex items-center justify-between flex-wrap gap-4 pb-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1 gradient-text">
              Loan Management
            </h1>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              Manage loan applications, approvals, and EMI tracking
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg" data-testid="apply-loan-button">
                <Plus className="w-4 h-4 mr-2" />
                Apply for Loan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full p-4 md:p-6">
              <DialogHeader className="pb-2">
                <DialogTitle className="text-xl md:text-2xl gradient-text">New Loan Application</DialogTitle>
                <DialogDescription className="text-sm">
                  Apply for a loan on behalf of an account holder
                </DialogDescription>
              </DialogHeader>

              {/* Form Progress Indicator */}
              <div className="flex items-center justify-center gap-1 py-2 mb-2 border-b">
                <div className="flex items-center gap-1">
                  <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-medium">1</span>
                  <span className="text-xs text-gray-600 hidden sm:inline">Details</span>
                </div>
                <div className="w-6 sm:w-8 h-0.5 bg-emerald-200"></div>
                <div className="flex items-center gap-1">
                  <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-medium">2</span>
                  <span className="text-xs text-gray-600 hidden sm:inline">Loan</span>
                </div>
                <div className="w-6 sm:w-8 h-0.5 bg-emerald-200"></div>
                <div className="flex items-center gap-1">
                  <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-medium">3</span>
                  <span className="text-xs text-gray-600 hidden sm:inline">Guarantors</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Member Selection */}
                <div className="space-y-3 p-3 md:p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                  <h3 className="font-semibold text-gray-800 text-sm md:text-base">Account Holder</h3>

                  <div className="space-y-1">
                    <Label htmlFor="account_holder_id" className="text-sm">Select Member *</Label>
                    <Select
                      value={formData.account_holder_id}
                      onValueChange={(value) => setFormData({ ...formData, account_holder_id: value })}
                      required
                    >
                      <SelectTrigger data-testid="member-select" className="h-9">
                        <SelectValue placeholder="Choose account holder" />
                      </SelectTrigger>
                      <SelectContent>
                        {members.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {formatFullName(member.first_name, member.middle_name, member.last_name)} ({member.account_number})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Borrower Identity */}
                <div className="space-y-3 p-3 md:p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                  <h3 className="font-semibold text-gray-800 text-sm md:text-base">Borrower Identity</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="borrower_aadhar_card" className="text-sm">Aadhar Card Number (Optional)</Label>
                      <Input
                        id="borrower_aadhar_card"
                        value={formData.borrower_aadhar_card}
                        onChange={(e) => setFormData({ ...formData, borrower_aadhar_card: e.target.value })}
                        placeholder="12-digit Aadhar number"
                        maxLength="12"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="borrower_pan_card" className="text-sm">PAN Card Number (Optional)</Label>
                      <Input
                        id="borrower_pan_card"
                        value={formData.borrower_pan_card}
                        onChange={(e) => setFormData({ ...formData, borrower_pan_card: e.target.value.toUpperCase() })}
                        placeholder="10-character PAN number"
                        maxLength="10"
                        className="h-9"
                      />
                    </div>
                  </div>
                </div>

                {/* Loan Details */}
                <div className="space-y-3 p-3 md:p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                  <h3 className="font-semibold text-gray-800 text-sm md:text-base">Loan Details</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="principal_amount" className="text-sm">Loan Amount (₹) *</Label>
                      <Input
                        id="principal_amount"
                        type="number"
                        step="0.01"
                        value={formData.principal_amount}
                        onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
                        required
                        placeholder="50000"
                        className="h-9"
                        data-testid="loan-amount-input"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="monthly_interest_rate" className="text-sm">Monthly Interest Rate (%) *</Label>
                      <Input
                        id="monthly_interest_rate"
                        type="number"
                        step="0.01"
                        value={formData.monthly_interest_rate}
                        onChange={(e) => setFormData({ ...formData, monthly_interest_rate: e.target.value })}
                        required
                        placeholder="0.5"
                        className="h-9"
                      />
                      <p className="text-xs text-gray-500">
                        Default is 0.5% per month
                      </p>
                    </div>
                  </div>

                  {formData.principal_amount && formData.tenure_months && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="grid grid-cols-2 gap-2 text-xs md:text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Principal:</span>
                          <span className="font-semibold">{formatCurrency(parseFloat(formData.principal_amount) || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Interest:</span>
                          <span className="font-semibold text-red-600">
                            - {formatCurrency(calculateLoanDetails().interest)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Payable:</span>
                          <span className="font-semibold text-emerald-600">
                            {formatCurrency(calculateLoanDetails().totalRepayment)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Monthly EMI:</span>
                          <span className="font-semibold">
                            {formatCurrency(calculateLoanDetails().monthlyInstallment)}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-amber-700 mt-1">
                        ℹ️ Interest = Principal * (Monthly Rate/100) * Tenure
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                    <div className="space-y-1">
                      <Label htmlFor="tenure_months" className="text-sm">Tenure (Months) *</Label>
                      <Input
                        id="tenure_months"
                        type="number"
                        value={formData.tenure_months}
                        className="h-9"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value && parseInt(value) > 20) {
                            toast.error('Maximum tenure allowed is 20 months as per society rules');
                            return;
                          }
                          setFormData({ ...formData, tenure_months: value });
                        }}
                        required
                        min="1"
                        max="20"
                        data-testid="tenure-input"
                      />
                      <p className="text-xs text-amber-600 font-medium">
                        ⚠️ Maximum 20 months as per society rules
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>EMI Start Date</Label>
                    <div className="h-10 px-3 py-2 bg-gray-100 rounded-md border border-gray-200 text-gray-700 flex items-center">
                      20th of Next Month (Auto-scheduled)
                    </div>
                  </div>

                  {formData.principal_amount && formData.tenure_months && (
                    <div className="p-4 bg-white rounded-lg border-2 border-emerald-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Disbursed Amount (On Hand):</span>
                        <span className="text-xl font-bold text-emerald-600">
                          {formatCurrency(calculateLoanDetails().disbursedAmount)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">Monthly EMI:</span>
                        <span className="text-2xl font-bold text-emerald-600">
                          {formatCurrency(calculateLoanDetails().monthlyInstallment)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Total Payable: {formatCurrency(calculateLoanDetails().totalRepayment)} (Interest already deducted upfront)
                      </p>
                    </div>
                  )}
                </div>

                {/* Guarantor Section */}
                <div className="space-y-3 p-3 md:p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-orange-600" />
                    <h3 className="text-base md:text-lg font-semibold text-orange-800">Guarantor Information *</h3>
                  </div>
                  <p className="text-xs md:text-sm text-orange-700 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Two guarantors required. Tap to expand and fill details.</span>
                  </p>

                  {/* Guarantor 1 - Collapsible */}
                  <Collapsible open={guarantor1Open} onOpenChange={setGuarantor1Open}>
                    <CollapsibleTrigger asChild>
                      <div className="p-3 bg-white rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-orange-800">Guarantor 1</h4>
                            {formData.guarantor1_name && (
                              <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">
                                {formData.guarantor1_name}
                              </Badge>
                            )}
                          </div>
                          {guarantor1Open ? (
                            <ChevronUp className="h-5 w-5 text-orange-600" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-orange-600" />
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <div className="p-3 bg-white rounded-lg border border-orange-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="guarantor1_name" className="text-sm">Full Name *</Label>
                            <Input
                              id="guarantor1_name"
                              type="text"
                              value={formData.guarantor1_name || ''}
                              onChange={(e) => setFormData({ ...formData, guarantor1_name: e.target.value })}
                              required
                              placeholder="Enter full name"
                              className="h-9"
                              data-testid="guarantor1-name-input"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="guarantor1_phone" className="text-sm">Phone Number *</Label>
                            <Input
                              id="guarantor1_phone"
                              type="tel"
                              value={formData.guarantor1_phone || ''}
                              onChange={(e) => setFormData({ ...formData, guarantor1_phone: e.target.value })}
                              required
                              placeholder="10-digit mobile"
                              pattern="[0-9]{10}"
                              maxLength="10"
                              className="h-9"
                              data-testid="guarantor1-phone-input"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="guarantor1_id_number" className="text-sm">Aadhaar / PAN (Optional)</Label>
                            <Input
                              id="guarantor1_id_number"
                              type="text"
                              value={formData.guarantor1_id_number || ''}
                              onChange={(e) => setFormData({ ...formData, guarantor1_id_number: e.target.value.toUpperCase() })}
                              placeholder="AADHAAR or PAN"
                              className="h-9"
                              data-testid="guarantor1-id-input"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="guarantor1_relationship" className="text-sm">Relationship *</Label>
                            <Input
                              id="guarantor1_relationship"
                              type="text"
                              value={formData.guarantor1_relationship || ''}
                              onChange={(e) => setFormData({ ...formData, guarantor1_relationship: e.target.value })}
                              required
                              placeholder="Friend, Neighbor, etc."
                              className="h-9"
                              data-testid="guarantor1-relationship-input"
                            />
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Guarantor 2 - Collapsible */}
                  <Collapsible open={guarantor2Open} onOpenChange={setGuarantor2Open}>
                    <CollapsibleTrigger asChild>
                      <div className="p-3 bg-white rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-orange-800">Guarantor 2</h4>
                            {formData.guarantor2_name && (
                              <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">
                                {formData.guarantor2_name}
                              </Badge>
                            )}
                          </div>
                          {guarantor2Open ? (
                            <ChevronUp className="h-5 w-5 text-orange-600" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-orange-600" />
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <div className="p-3 bg-white rounded-lg border border-orange-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="guarantor2_name" className="text-sm">Full Name *</Label>
                            <Input
                              id="guarantor2_name"
                              type="text"
                              value={formData.guarantor2_name || ''}
                              onChange={(e) => setFormData({ ...formData, guarantor2_name: e.target.value })}
                              required
                              placeholder="Enter full name"
                              className="h-9"
                              data-testid="guarantor2-name-input"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="guarantor2_phone" className="text-sm">Phone Number *</Label>
                            <Input
                              id="guarantor2_phone"
                              type="tel"
                              value={formData.guarantor2_phone || ''}
                              onChange={(e) => setFormData({ ...formData, guarantor2_phone: e.target.value })}
                              required
                              placeholder="10-digit mobile"
                              pattern="[0-9]{10}"
                              maxLength="10"
                              className="h-9"
                              data-testid="guarantor2-phone-input"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="guarantor2_id_number" className="text-sm">Aadhaar / PAN (Optional)</Label>
                            <Input
                              id="guarantor2_id_number"
                              type="text"
                              value={formData.guarantor2_id_number || ''}
                              onChange={(e) => setFormData({ ...formData, guarantor2_id_number: e.target.value.toUpperCase() })}
                              placeholder="AADHAAR or PAN"
                              className="h-9"
                              data-testid="guarantor2-id-input"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="guarantor2_relationship" className="text-sm">Relationship *</Label>
                            <Input
                              id="guarantor2_relationship"
                              type="text"
                              value={formData.guarantor2_relationship || ''}
                              onChange={(e) => setFormData({ ...formData, guarantor2_relationship: e.target.value })}
                              required
                              placeholder="Friend, Neighbor, etc."
                              className="h-9"
                              data-testid="guarantor2-relationship-input"
                            />
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>

                {/* Sticky Submit Button Area */}
                <div className="sticky bottom-0 bg-white pt-3 pb-1 -mx-4 md:-mx-6 px-4 md:px-6 border-t shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 h-10"
                      data-testid="submit-loan-button"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Application'
                      )}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 h-10" disabled={submitting}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Loan Schedule Dialog */}
        <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
          <DialogContent className="w-[95vw] max-w-4xl sm:w-full max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl md:text-2xl gradient-text pr-8">EMI Schedule</DialogTitle>
              <DialogDescription>
                Complete loan amortization schedule
              </DialogDescription>
            </DialogHeader>

            {/* Mobile scroll hint */}
            <p className="text-xs text-gray-500 md:hidden flex items-center gap-1 mt-2">
              <span>←</span> Scroll horizontally to see all columns <span>→</span>
            </p>

            <div className="mt-4 relative">
              {/* Scroll shadow indicator for mobile */}
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none z-10 md:hidden" />
              <div className="overflow-x-auto -mx-2 px-2">
                <Table className="min-w-[600px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap px-2 sm:px-4">EMI #</TableHead>
                      <TableHead className="whitespace-nowrap px-2 sm:px-4">Due Date</TableHead>
                      <TableHead className="whitespace-nowrap px-2 sm:px-4">EMI Amt</TableHead>
                      <TableHead className="whitespace-nowrap px-2 sm:px-4">Principal</TableHead>
                      <TableHead className="whitespace-nowrap px-2 sm:px-4">Interest</TableHead>
                      <TableHead className="whitespace-nowrap px-2 sm:px-4">Balance</TableHead>
                      <TableHead className="whitespace-nowrap px-2 sm:px-4">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedLoanSchedule.map((item) => (
                      <TableRow key={item.installment_no}>
                        <TableCell className="font-medium px-2 sm:px-4">{item.installment_no}</TableCell>
                        <TableCell className="whitespace-nowrap px-2 sm:px-4">{formatDate(item.due_date)}</TableCell>
                        <TableCell className="whitespace-nowrap px-2 sm:px-4">{formatCurrency(item.emi_amount)}</TableCell>
                        <TableCell className="whitespace-nowrap px-2 sm:px-4">{formatCurrency(item.principal_component)}</TableCell>
                        <TableCell className="whitespace-nowrap px-2 sm:px-4">{formatCurrency(item.interest_component)}</TableCell>
                        <TableCell className="whitespace-nowrap px-2 sm:px-4">{formatCurrency(item.balance_after)}</TableCell>
                        <TableCell className="px-2 sm:px-4">
                          <Badge className={getStatusColor(item.payment_status)}>
                            {item.payment_status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Search Bar */}
        <Card className="glass border-0 shadow-soft">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Search by loan account number, member name..."
                className="pl-12 h-12"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass border-0 shadow-soft card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Active Loans</p>
                  <p className="text-xl md:text-3xl font-bold text-emerald-600 mt-1">{quickStats.activeLoans}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-0 shadow-soft card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Total Outstanding</p>
                  <p className="text-lg md:text-2xl font-bold text-blue-600 mt-1">{formatCurrency(quickStats.totalOutstanding)}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-0 shadow-soft card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Overdue Loans</p>
                  <p className="text-xl md:text-3xl font-bold text-orange-600 mt-1">{quickStats.overdueCount}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-0 shadow-soft card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Due This Month (20th)</p>
                  <p className="text-xl md:text-3xl font-bold text-purple-600 mt-1">{quickStats.dueThisMonth}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Sort */}
        <Card className="glass border-0 shadow-soft">
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Filter Header */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-800">Filters & Sort</h3>
                  <Badge variant="outline" className="ml-2">
                    {filteredAndSortedLoans.length} loans
                  </Badge>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className="text-xs"
                >
                  Reset All
                </Button>
              </div>

              {/* Filter Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Status Filter */}
                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Status</Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) => setFilters({ ...filters, status: value })}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Overdue Toggle */}
              <div className="flex items-center gap-2 pt-2 border-t">
                <input
                  type="checkbox"
                  id="showOverdue"
                  checked={filters.showOverdue}
                  onChange={(e) => setFilters({ ...filters, showOverdue: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <Label htmlFor="showOverdue" className="text-sm cursor-pointer">
                  Show only overdue/active loans
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="skeleton h-32"></Card>
            ))}
          </div>
        ) : filteredAndSortedLoans.length === 0 ? (
          <Card className="glass border-0 shadow-soft">
            <CardContent className="pt-12 pb-12">
              <div className="text-center">
                {loans.length === 0 ? (
                  <>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No Loans Yet</h3>
                    <p className="text-gray-600 mb-6">Apply for your first loan to get started</p>
                    <Button onClick={() => setDialogOpen(true)} className="bg-gradient-to-r from-emerald-600 to-teal-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Apply for Loan
                    </Button>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No Loans Match Filters</h3>
                    <p className="text-gray-600 mb-6">Try adjusting your filters or search query</p>
                    <Button onClick={resetFilters} variant="outline">
                      <Filter className="w-4 h-4 mr-2" />
                      Reset Filters
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredAndSortedLoans.map((loan, index) => (
              <Card
                key={loan.id}
                className="glass card-hover border-0 shadow-soft hover:shadow-medium"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`loan-card-${loan.id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg text-white font-bold text-lg md:text-2xl shrink-0">
                        {loan.account_holder_name ? loan.account_holder_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'LN'}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-800">{loan.account_holder_name}</h3>
                        <p className="text-sm text-gray-600">Loan: {loan.loan_account_number}</p>
                        <p className="text-xs text-gray-500">Member ID: {loan.account_holder_account_number}</p>
                        <Badge className={`mt-1 ${getStatusColor(loan.status)}`}>
                          {loan.status}
                        </Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div>
                        <p className="text-xs text-gray-500">Loan Amount</p>
                        <p className="font-bold text-gray-800">{formatCurrency(loan.principal_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">EMI</p>
                        <p className="font-bold text-gray-800">{formatCurrency(loan.emi_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Outstanding</p>
                        <p className="font-bold text-orange-600">{formatCurrency(loan.outstanding_principal)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">EMIs Left</p>
                        <p className="font-bold text-blue-600">{loan.outstanding_installments}/{loan.tenure_months}</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {loan.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleApproveLoan(loan.id)}
                          disabled={approvingLoan === loan.id}
                          className="bg-gradient-to-r from-green-600 to-emerald-600"
                        >
                          {approvingLoan === loan.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Approving...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/manager/loans/${loan.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewSchedule(loan.id)}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default LoanManagement;
